import * as React from 'react';
import { useContext, useState } from 'react';

import { withFormik } from 'formik';

import { useNavigateToRoute } from '../../../../shared/hooks/use-navigate-to-route/use-navigate-to-route.hook';
import { useAppDispatch } from '../../../../store/hooks';
import { SnackBarContext } from '../../../shared/components/snack-bar-context/SnackBarContext';
import { SnackBarContextModel } from '../../../shared/components/snack-bar-context/models/snack-bar-context.model';
import useChangePrompt from '../../../shared/hooks/use-change-prompt/use-change-prompt.hook';
import { useCheckPath } from '../../../shared/hooks/use-check-path/use-check-path.hook';
import { useGetForm } from '../../../shared/hooks/use-get-form/use-get-form.hook';
import FirmwareDetailsForm from '../FirmwareDetailsForm/FirmwareDetailsForm';
import { FirmwareDetailsFormModel } from '../FirmwareDetailsForm/models/firmware-form.model';
import { OuterFirmwareFormPropsModel } from '../FirmwareDetailsForm/models/outer-firmware-form-props.model';
import { useGetFirmwareFileSelectors } from './hooks/use-get-firmware-file-selectors/use-get-firmware-file-selectors';
import { useHandleSubmitFirmwareForm } from './hooks/use-handle-on-submit-firmware-form/use-handle-submit-firmware-form.hook';
import { usePopulateEditFirmwareForm } from './hooks/use-populate-edit-firmware-form/use-populate-edit-firmware-form.hook';
import { getFirmwareFormValidationSchema } from './schemas/get-firmware-form-validation.schema';

const FirmwareForm = (): JSX.Element => {
  const { firmwareFileId, firmwareFile } = useGetFirmwareFileSelectors();
  const [isOnEdit, { id: firmwareId }] = useCheckPath(
    '/firmware-management/edit/'
  );

  const [formFields, setFormFields] = useState<FirmwareDetailsFormModel>({
    name: '',
    platform: null,
    brand: null,
    model: null,
    ecu: null,
    description: '',
    versionName: '',
    versionCode: '',
    status: null,
  });

  const navigateFn = useNavigateToRoute('../firmwares');
  const dispatch = useAppDispatch();
  const snackBarContext = useContext(SnackBarContext) as SnackBarContextModel;

  usePopulateEditFirmwareForm(firmwareId, isOnEdit, setFormFields);

  const cancelAction = useChangePrompt(
    true,
    navigateFn,
    <span>Warning</span>,
    <span>Filled information will be lost. Leave anyway?</span>
  );

  const withFirmwareFormik = withFormik<
    OuterFirmwareFormPropsModel,
    FirmwareDetailsFormModel
  >({
    mapPropsToValues: () => formFields,

    validationSchema: (props: OuterFirmwareFormPropsModel) =>
      getFirmwareFormValidationSchema(props.firmwareFileId),

    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: false,

    handleSubmit: useHandleSubmitFirmwareForm(isOnEdit, firmwareId),
  });

  const ReturnedComponent = useGetForm(
    withFirmwareFormik,
    FirmwareDetailsForm,
    formFields
  );

  return (
    <ReturnedComponent
      isOnEdit={isOnEdit}
      firmwareId={firmwareId}
      dispatch={dispatch}
      snackBarContext={snackBarContext}
      cancelAction={cancelAction}
      firmwareFileId={firmwareFileId}
      firmwareFile={firmwareFile}
    />
  );
};

export default FirmwareForm;
